import type { ContextBalances, PairBalance } from './contextApi';

/** The pairs the user is part of, as debtor or creditor. */
export const pairsInvolving = (balances: ContextBalances, userId: string): PairBalance[] =>
  balances.pairs.filter((pair) => pair.fromUserId === userId || pair.toUserId === userId);

export interface ContextStanding {
  contextId: string;
  contextName: string;
  currency: string;
  /** Owed to the user in this context. */
  receivable: number;
  /** Owed by the user in this context. */
  payable: number;
}

export interface BalanceSummary {
  /** Totals per currency: groups in different currencies never add up. */
  totals: Array<{ currency: string; receivable: number; payable: number }>;
  contexts: ContextStanding[];
}

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * What the user has to receive and to pay across every group, from the
 * pairwise balances. Contexts where nothing involves the user are left out.
 */
export function summarizeBalances(all: ContextBalances[], userId: string): BalanceSummary {
  const contexts: ContextStanding[] = [];
  const byCurrency = new Map<string, { receivable: number; payable: number }>();

  for (const balances of all) {
    const pairs = pairsInvolving(balances, userId);
    if (pairs.length === 0) {
      continue;
    }
    const receivable = round(
      pairs.filter((pair) => pair.toUserId === userId).reduce((sum, pair) => sum + pair.amount, 0),
    );
    const payable = round(
      pairs.filter((pair) => pair.fromUserId === userId).reduce((sum, pair) => sum + pair.amount, 0),
    );
    contexts.push({
      contextId: balances.contextId,
      contextName: balances.contextName,
      currency: balances.currency,
      receivable,
      payable,
    });

    const current = byCurrency.get(balances.currency) ?? { receivable: 0, payable: 0 };
    byCurrency.set(balances.currency, {
      receivable: round(current.receivable + receivable),
      payable: round(current.payable + payable),
    });
  }

  return {
    totals: Array.from(byCurrency.entries()).map(([currency, totals]) => ({ currency, ...totals })),
    contexts,
  };
}

/** A member's first name, or the "you" label for the signed-in user. */
export const memberFirstName = (
  balances: ContextBalances,
  userId: string,
  selfId: string | undefined,
  youLabel: string,
): string =>
  userId === selfId
    ? youLabel
    : balances.members.find((member) => member.userId === userId)?.firstName ?? '?';

/** First names of the members in open pairs who were invited and have not joined yet. */
export const pendingNames = (balances: ContextBalances, selfId: string | undefined): string[] => {
  const involved = new Set(balances.pairs.flatMap((pair) => [pair.fromUserId, pair.toUserId]));
  return balances.members
    .filter((member) => member.pending && member.userId !== selfId && involved.has(member.userId))
    .map((member) => member.firstName);
};
